import { SectionHeader, SectionWrap } from "@/components/Section";
import {
  Microscope,
  Atom,
  Cpu,
  HeartPulse,
  Brain,
  Plane,
  Briefcase,
  Code2,
  ShieldCheck,
} from "lucide-react";

// Anonymized cards — roles + affiliations only. Names available under NDA.
const TEAM = [
  {
    icon: Brain,
    role: "Chief Executive Officer",
    field: "Neuroscience",
    sub: "Assistant Professor · Neuroscience",
    color: "#38BDF8",
  },
  {
    icon: Microscope,
    role: "Chief Science Officer",
    field: "Neuroscience",
    sub: "Discipline Chair · Neuroscience",
    color: "#2563EB",
  },
  {
    icon: Cpu,
    role: "Chief Technology Officer",
    field: "Brain Science",
    sub: "Director · Brain Science Institute",
    color: "#6366F1",
  },
  {
    icon: Atom,
    role: "Chief Innovation Officer",
    field: "Molecular Therapeutics",
    sub: "Director · Proteomics & Molecular Therapeutics",
    color: "#8B5CF6",
  },
  {
    icon: HeartPulse,
    role: "Chief Medical Officer",
    field: "Clinical Medicine",
    sub: "Associate Professor of Medicine",
    color: "#38BDF8",
  },
  {
    icon: Plane,
    role: "Chief AI Officer",
    field: "Engineering & AI",
    sub: "Aerospace Engineer · Inventor",
    color: "#2563EB",
  },
  {
    icon: Briefcase,
    role: "Chief Business Officer",
    field: "Operations",
    sub: "Founder & President · Consulting",
    color: "#6366F1",
  },
  {
    icon: Code2,
    role: "Chief IT Officer",
    field: "Molecular Neuroscience",
    sub: "Research · Molecular Neuroscience",
    color: "#8B5CF6",
  },
];

const STATS = [
  { k: "~200", v: "Peer-reviewed publications" },
  { k: ">$30M", v: "NIH & national funding" },
  { k: "5", v: "Startups founded" },
  { k: ">100", v: "Years of neuro experience" },
];

export function TeamSection({ onRequestDossier }) {
  return (
    <SectionWrap id="team">
      <SectionHeader
        num="06"
        kicker="The Team"
        title="Built by scientists, clinicians, engineers, and operators."
        sub="Our founding team brings depth across neuroscience, medicine, molecular therapeutics, proteomics, AI, aerospace engineering, business development, and biomedical innovation."
      />

      {/* Credibility metrics */}
      <div className="reveal mt-14 grid grid-cols-2 md:grid-cols-4 gap-px bg-white/5 border border-white/5 rounded-2xl overflow-hidden">
        {STATS.map((s, i) => (
          <div key={i} className="bg-[#07111F] p-7">
            <div
              className="font-display text-[34px] lg:text-[40px] font-medium bg-clip-text text-transparent"
              style={{
                backgroundImage:
                  "linear-gradient(120deg,#38BDF8 0%, #6366F1 60%, #8B5CF6 100%)",
              }}
            >
              {s.k}
            </div>
            <div className="mt-2 text-[#94A3B8] text-[13px] tracking-wide">
              {s.v}
            </div>
          </div>
        ))}
      </div>

      {/* Anonymized leadership grid */}
      <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {TEAM.map(({ icon: Icon, role, field, sub, color }, i) => (
          <article
            key={i}
            className="reveal glass rounded-2xl p-6 group hover:border-[#38BDF8]/30 transition-colors"
            style={{ transitionDelay: `${(i % 4) * 50}ms` }}
          >
            <div className="flex items-center justify-between">
              <div
                className="h-11 w-11 rounded-xl grid place-items-center"
                style={{
                  background: `linear-gradient(140deg, ${color}33, transparent 70%), #0F172A`,
                  border: `1px solid ${color}55`,
                  color,
                }}
              >
                <Icon size={18} strokeWidth={1.7} />
              </div>
              <span className="font-mono text-[10px] tracking-[0.3em] text-[#475569]">
                0{i + 1}
              </span>
            </div>
            <div className="mt-5 font-mono text-[10px] uppercase tracking-[0.28em] text-[#64748B]">
              {field}
            </div>
            <h4 className="mt-2 font-display text-white text-[17px] leading-tight">
              {role}
            </h4>
            <div className="mt-3 text-[12.5px] text-[#94A3B8] leading-relaxed">
              {sub}
            </div>
          </article>
        ))}
      </div>

      {/* Privacy disclosure */}
      <div className="reveal mt-10 flex flex-col md:flex-row items-start md:items-center gap-4 rounded-2xl border border-dashed border-white/10 p-5">
        <div className="h-10 w-10 rounded-xl grid place-items-center bg-[#38BDF8]/10 border border-[#38BDF8]/30 text-[#38BDF8] shrink-0">
          <ShieldCheck size={18} strokeWidth={1.7} />
        </div>
        <p className="text-[13.5px] text-[#CBD5E1] leading-relaxed flex-1">
          <span className="text-white font-medium">Team identities are private at this stage.</span>{" "}
          Full names, biographies, publications, and references are available
          to qualified research, clinical, and investment partners under NDA.
        </p>
        <button
          data-testid="team-request-dossier"
          onClick={onRequestDossier}
          className="btn-glow inline-flex shrink-0 items-center gap-2 px-4 py-2.5 rounded-full bg-white text-[#020617] text-[13px] font-semibold hover:bg-[#BFDBFE]"
        >
          Request team dossier
        </button>
      </div>
    </SectionWrap>
  );
}
